import axios from 'axios';

// Determine the appropriate API URL based on the environment
const determineBaseUrl = () => {
  // First check for explicit environment variable
  const envApiUrl = import.meta.env.VITE_API_URL;
  if (envApiUrl) {
    console.log(`[API] Using API URL from environment: ${envApiUrl}`);
    // Make sure the URL ends with /api
    return envApiUrl.endsWith('/api') ? envApiUrl : `${envApiUrl}/api`;
  }
  
  // Get the current protocol
  const protocol = window.location.protocol;
  console.log(`[API] Current protocol: ${protocol}`);
  
  // For development, use the current protocol and hostname
  if (import.meta.env.DEV) {
    // Always use the same protocol as the page for API requests
    console.log('[API] Using same protocol as page for development');
    return `${window.location.protocol}//${window.location.host}/api`;
  }
  
  // For production, use the environment variable if available
  const publicApiUrl = import.meta.env.VITE_PUBLIC_API_URL;
  console.log(`[API] VITE_PUBLIC_API_URL: ${publicApiUrl}`);
  
  if (publicApiUrl) {
    // If it includes protocol, use as is
    if (publicApiUrl.includes('://')) {
      console.log(`[API] Using provided URL with protocol: ${publicApiUrl}/api`);
      return `${publicApiUrl}/api`;
    }
    // Always use HTTPS protocol
    console.log(`[API] Using HTTPS with provided URL: https://${publicApiUrl}/api`);
    return `https://${publicApiUrl}/api`;
  }
  
  // Default to relative URL for same-origin requests
  console.log('[API] Using default relative path');
  return '/api';
};

let baseURL = determineBaseUrl();

// Ensure all requests use the same protocol as the page
const protocol = window.location.protocol;
if (protocol === 'https:' && baseURL.startsWith('http:')) {
  console.log(`[API] Upgrading base URL from HTTP to HTTPS: ${baseURL}`);
  baseURL = baseURL.replace('http:', 'https:');
}

// For absolute security, if we're on HTTPS, force all URLs to be HTTPS
if (protocol === 'https:' && baseURL.includes('://')) {
  baseURL = baseURL.replace(/^http:\/\//i, 'https://');
}

console.log(`[API] Using base URL: ${baseURL}`);

// Create a request queue for throttling
const requestQueue = [];
let isProcessingQueue = false;
const MAX_CONCURRENT_REQUESTS = 5;
let activeRequests = 0;

// Process the queue with throttling
const processQueue = () => {
  if (isProcessingQueue || requestQueue.length === 0 || activeRequests >= MAX_CONCURRENT_REQUESTS) return;
  
  isProcessingQueue = true;
  
  const { config, resolve, reject } = requestQueue.shift();
  activeRequests++;
  
  axios(config)
    .then(response => {
      resolve(response);
    })
    .catch(error => {
      reject(error);
    })
    .finally(() => {
      activeRequests--;
      isProcessingQueue = false;
      
      // Process next request if any
      if (requestQueue.length > 0) {
        setTimeout(processQueue, 200); // Add a small delay between requests
      }
    });
};

// Create a custom axios instance with the correct baseURL
const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds
  maxRedirects: 5, // Allow up to 5 redirects
  withCredentials: true, // Send cookies with cross-origin requests
});

// Ensure withCredentials is set for all requests
api.defaults.withCredentials = true;

// Override the default axios instance
axios.defaults.baseURL = baseURL;

// Request interceptor for adding auth token
api.interceptors.request.use(
  async (config) => {
    // Initialize headers if not present
    config.headers = config.headers || {};
    
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Don't add trailing slash to any endpoints - they cause redirect issues
    
    // Get the current user and CSRF token
    let currentUser;
    let csrfToken;
    
    try {
      // First try to get from localStorage
      currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      csrfToken = currentUser?.csrf_token;
      
      // For admin routes, always try to get a fresh token
      if (config.url?.includes('/admin/') && (!csrfToken || config.url?.includes('audit-logs'))) {
        console.log(`[API] Admin route detected: ${config.url}, fetching fresh CSRF token`);
        try {
          // Make a request to get the current user and CSRF token
          const authResponse = await axios.get(`${baseURL}/auth/me`, { 
            withCredentials: true,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          
          if (authResponse.data && authResponse.data.csrf_token) {
            csrfToken = authResponse.data.csrf_token;
            console.log(`[API] Got fresh CSRF token for admin route: ${csrfToken.substring(0, 10)}...`);
            
            // Update localStorage
            localStorage.setItem('currentUser', JSON.stringify(authResponse.data));
          }
        } catch (error) {
          console.error('[API] Failed to get fresh CSRF token:', error);
        }
      }
    } catch (error) {
      console.error('[API] Error parsing currentUser from localStorage:', error);
    }
    
    // Ensure CSRF token is included
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
      
      // For admin routes, also add as Bearer token
      if (config.url?.includes('/admin/')) {
        config.headers.Authorization = `Bearer ${csrfToken}`;
      }
    }
    
    // Always add these headers for better CORS handling
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
    
    // Always ensure cookies are sent
    config.withCredentials = true;
    
    // Only log CSRF token warnings for important requests
    if (!config.headers['X-CSRF-Token'] && config.url?.includes('/admin/')) {
      console.warn(`[API] No CSRF token for admin route: ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor rejection:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    // Only log non-success responses or important endpoints
    if (response.status >= 400 || response.config.url?.includes('/admin/')) {
      console.log(`[API] Response from ${response.config.url}:`, {
        status: response.status,
        statusText: response.statusText
      });
    }
    
    return response;
  },
  async (error) => {
    // Don't retry auth endpoints on 401/403 errors
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    const isAuthError = error.response?.status === 401 || error.response?.status === 403;
    
    // Log the error details
    console.error(`[API] Error response from ${error.config?.url}:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    // Handle rate limiting (429)
    if (error.response && error.response.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 2;
      const retryDelayMs = parseInt(retryAfter, 10) * 1000;
      
      console.warn(`Rate limit exceeded. Retrying after ${retryDelayMs}ms`);
      
      // Only retry non-auth endpoints or auth endpoints that aren't returning auth errors
      if (!isAuthEndpoint || !isAuthError) {
        // Wait for the specified delay
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        
        // Retry the request
        return api(error.config);
      }
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED' || error.message.includes('Network Error')) {
      console.error('[API] Network error');
    }
    
    // Handle 401 Unauthorized for non-auth endpoints
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Check if we're already on the login page to prevent redirect loops
      const isOnLoginPage = window.location.pathname.includes('/login');
      
      // Also check if this is an auth check endpoint
      const isAuthCheckEndpoint = error.config?.url?.includes('/auth/me');

      // Never redirect for consent endpoints — they may 401 transiently during
      // the CSRF token propagation window and are handled silently by ConsentModal
      const isConsentEndpoint = error.config?.url?.includes('/consent/');
      const isVersionEndpoint = error.config?.url?.includes('/version/');
      
      // NEVER redirect if we're on the login page or checking auth status
      if (!isOnLoginPage && !isAuthCheckEndpoint && !isConsentEndpoint && !isVersionEndpoint) {
        console.log('[API] Not on login page, redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        console.log('[API] Already on login page or auth check endpoint, not redirecting to prevent loop');
      }
    }
    
    console.error('API Error:', error.message);
    return Promise.reject(error);
  }
);

export default api;
